import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { AssetContainer, HTMLAsset } from '@techstyle/react-assets';
import { FormattedMessage } from '@techstyle/react-intl';

import { desktop, mobile } from '../../styles';
import { toStringSet } from '../../utils/assets';
import { useLDFlags } from '../../utils/LD/useLDFlags';
import {
  getProductPromos,
  getRegion,
  getShouldShowPromoPrice,
  getWarehouseQuantity,
  isBackInStockProduct,
  isBestSellerProduct,
  isClearanceItem as checkIsClearanceItem,
  isClfProduct,
  isComingSoonProduct as checkIsComingSoonProduct,
  isExclusiveProduct,
  isFWProduct,
  isNewProduct,
  isPreorderable as checkIsPreorderable,
  isProductCustomizable,
  isProductSet as checkIsProductSet,
  isRihannasPickProduct,
  isSoldOut as checkIsSoldOut,
  isTopRatedProduct,
  isTrendingProduct,
  isUnlockedXclusiveProduct,
  isYouniversalProduct,
  shouldHideRelatedProducts as checkShouldHideRelatedProducts,
  shouldProductUseCustomTag,
} from '../../utils/selectors';
import { useMembership } from '../../utils/useMembership';
import { Component as CLFFlag } from '../CLFFlag';
import { Component as Price } from '../ExtendedPrice';
import { Component as FWFlag } from '../FWFlag';
import { Component as LowStockFlag } from '../LowStockFlag';
import { Component as ProductFlag } from '../ProductFlag';
import { Component as ProductImage } from '../ProductImage';
import { Container as ProductImpression } from '../ProductImpression';
import { Component as ProductLink } from '../ProductLink';
import { Component as Stars } from '../Stars';
import VipBoxButton from '../VipBoxButton/VipBoxButtonContainer';
import { Component as WishlistButton } from '../WishlistButton';
import { Component as WishlistContext } from '../WishlistContext';
import XclusiveFlag from '../XclusiveFlag/XclusiveFlag';

const locationLowStockIndicatorMasterProduct = config.get(
  'public.quantityRules.locationLowStockIndicator.masterProduct'
);
const multiColors = config.get('public.productTags.multiColors');

const Thumbnail = styled(ProductImage)`
  width: 100%;
  height: auto;
  vertical-align: top;
  opacity: 1;
`;

const ThumbnailImageWrapper = styled.div``;

const ThumbnailLaydown = styled(Thumbnail)`
  opacity: 0;
  position: absolute;
  top: 0;
  left: 0;
`;

const ProductCellLowStockFlag = styled(LowStockFlag)``;

const ThumbContainer = styled.div`
  position: relative;
  z-index: 1;
  > a {
    display: block;
  }
  ${props =>
    props.shouldShowSpacedBorder &&
    `
    padding: 10px;
    box-sizing: border-box;
    box-shadow: 0px 2px 6px ${props.theme.colors.lightShadow1};
    border-radius: 2px;

    @media (max-width: ${props.theme.breakpoints.large}px) {
      .slick-active & {
        border: 1px solid ${props.theme.colors.default};
        box-shadow: 0px 2px 4px ${props.theme.colors.lightShadow1};
      }
    }
  `}

  ${desktop`
    &:hover {
      & ${Thumbnail} {
        opacity: 0;
        transition: opacity 200ms ease-in-out;
      }
      & ${ThumbnailLaydown} {
        visibility: visible;
        opacity: 1;
        transition: opacity 200ms ease-in-out;
      }
    }
  `};
`;

const SoldOutOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  text-align: center;
  color: ${props => props.theme.colors.white};
  background: ${props => props.theme.colors.scrim};
  z-index: 1;
`;

const ComingSoonOverlay = styled(SoldOutOverlay)``;

const ThumbHeader = styled.div`
  display: flex;
  align-items: flex-start;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  /**
   * The height is 0 so that you can still click the underlying image to go to
   * the product, but the header contents are still positioned relative to the
   * top of this container.
   */
  height: 0;
  z-index: 2;
`;

const ThumbnailWrapper = styled.div`
  width: 100%;
  height: 0;
  padding-bottom: 100%;
  background: ${props => props.theme.colors.gray250};
  ${props =>
    props.showShadow
      ? css`
          box-shadow: 0 1px 2px 0 ${props.theme.colors.lightShadow3};
        `
      : null}
`;

const ThumbnailNewRatioWrapper = styled(ThumbnailWrapper)`
  padding-bottom: 0px;
  height: auto;
  aspect-ratio: 3/4;
`;

const Details = styled.div`
  ${desktop`
    min-height: ${props => (props.isOnGrid ? '170px' : 0)};
    padding: 10px 0 ${props => (props.isOnGrid ? '12px' : 0)}
  `}
  ${mobile`
    min-height: ${props => (props.isOnGrid ? '130px' : 0)};
  `}
    ${props => props.theme.spacing.tiny}px;
  color: ${props => props.theme.colors.default};
  ${desktop`
    padding-left: 0;
  `}
`;

const ProductName = styled.div`
  ${({ theme, $gridPdpPricingUpdatesEnabled }) => {
    if ($gridPdpPricingUpdatesEnabled) {
      return css`
        ${theme.paragraph.variants.paragraph4Uppercase.textStyles}
        color: ${theme.colors.black};
      `;
    } else if (theme.context.bca) {
      css`
        font-weight: 500;
        line-height: 18px;
        display: block;
        white-space: normal;
        color: ${theme.colors.black};
        max-width: 100%;
        margin: 4px 0 ${theme.spacing.tiny}px 0;
        ${theme.paragraph.variants.paragraph3Uppercase.textStyles};
        text-transform: uppercase;
      `;
    }

    return css`
      ${theme.paragraph.variants.paragraph3Uppercase.textStyles}
      font-weight: 500;
      line-height: 18px;
      display: block;
      white-space: normal;
      color: ${theme.colors.black};
      max-width: 100%;
      margin: 8px 0 ${theme.spacing.tiny}px 0;
      text-transform: uppercase;
    `;
  }}
`;

const ColorsText = styled.div`
  ${({ theme }) =>
    theme.context.bca
      ? `
        font-size: 12px;
        line-height: normal;
        color: ${theme.colors.subdued};
      
        &&& {
          text-decoration: none;
        }
      
        ${theme.paragraph.variants.paragraph5Uppercase.textStyles};
        text-transform: uppercase;
        text-align: left;
      `
      : `
        font-size: 12px;
        line-height: normal;
        color: ${theme.colors.subdued};
        ${theme.paragraph.variants.paragraph5Uppercase.textStyles};
        margin-top: 4px;
      `};
`;

const Container = styled.article.attrs(props => {
  return {
    className: `${props.className || ''} bfx-product bfx-price-product`.trim(),
  };
})`
  ${props =>
    props.shouldShowSpacedBorder &&
    `
    margin: 3px;
  `}

  ${props =>
    props.theme.context.bca &&
    props.renderSmall &&
    desktop`
      max-width: 231px;
    `}
`;

const PriceWrapper = styled.div`
  ${({ $gridPdpPricingUpdatesEnabled }) =>
    $gridPdpPricingUpdatesEnabled
      ? css`
          margin-top: 4px;
          display: flex;
          flex-wrap: wrap;
          gap: 4px;

          ${StyledPrice} {
            flex: unset;
          }
        `
      : css`
          display: flex;
          flex-direction: ${props => (props.isBundle ? 'row' : 'column')};
          ${props => (props.isBorderfreeCustomer ? `display: block;` : ``)}
          padding: 2px 0;
        `}
`;

const StyledPrice = styled(Price)`
  display: ${props => (props.isBorderfreeCustomer ? `inline-block` : `block`)};
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  padding: 0 0 2px;
  color: ${props =>
    props.isClearanceItem
      ? props.theme.colors.error
      : props.isPromoprice
      ? props.theme.colors.promo
      : props.theme.colors.black};
  flex: ${props => (props.isBundle ? 'none' : 1)};

  font-size: 14px;

  ${({ theme }) => theme.paragraph.variants.paragraph3.textStyles}
  font-weight: 500;
  color: ${({ theme }) => theme.colors.black};

  ${({ $showStrikethrough }) =>
    $showStrikethrough &&
    css`
      font-weight: 400;
      color: ${({ theme }) => theme.colors.grey};
      text-decoration: line-through;
    `}
`;

const StyledStars = styled(Stars)`
  display: block;
  line-height: 1;
  margin-top: 0px;

  svg {
    height: 11px;
    width: 11px;
  }
`;

const SoldOutContainer = styled.div``;
const ComingSoonContainer = styled.div``;

const FlagWrapper = styled.div`
  display: inline-block;
  margin-top: ${({ theme }) => theme.spacing.small}px !important;

  ${({ theme }) =>
    theme.context.bca &&
    `
      display: flex;
      margin-right: ${theme.spacing.tiny}px;
      background-color: ${theme.colors.white};
      span {
        background-color: ${theme.colors.white};
      }
      border-radius: 0;
      color: ${theme.colors.gunmetal};
      position: absolute;
      top: 0;
      left: ${theme.spacing.tiny}px;
  `};
`;

const OptionText = styled.span`
  font-weight: normal;
  color: ${props => props.theme.colors.subdued};
  margin-right: 3px;
`;

const PromoTextWrapper = styled.div`
  ${({ $gridPdpPricingUpdatesEnabled, theme }) => {
    if ($gridPdpPricingUpdatesEnabled) {
      return css`
        ${theme.paragraph.variants.paragraph5Uppercase.textStyles}
        color: ${theme.colors.memberOffers};

        ${PromoAsset} * {
          ${theme.paragraph.variants.paragraph5Uppercase.textStyles}
        }
      `;
    }

    return css`
      font-size: 14px;
      line-height: 0.86;
      padding: 0;
      font-weight: 500;
      color: ${props =>
        props.isClearanceItem
          ? props.theme.colors.error
          : props.theme.colors.promo};
      ${desktop`
    padding: 0 0 2px;
  `}
    `;
  }}
`;

const PromoText = styled.div`
  color: ${props => props.theme.colors.promo};
  font-size: 14px;
  line-height: 18px;
  font-weight: 500;
  flex: 1;
`;

const PromoTextBundle = styled(PromoText)`
  flex: 0 0 auto;
  margin-left: 3px;
  padding: 1px 0 0px;
`;

const PromoLabel = styled.div`
  ${({ $gridPdpPricingUpdatesEnabled, theme }) => {
    if ($gridPdpPricingUpdatesEnabled) {
      return css`
        ${theme.paragraph.variants.paragraph5Uppercase.textStyles}
        color: ${theme.colors.memberOffers};
      `;
    }

    return css`
      ${({ theme }) =>
        theme.context.recommendations
          ? `
        color: ${theme.colors.memberOffers};
      `
          : `
        color: ${theme.colors.lavender700};
      `};
      font-size: 10px;
      font-weight: 500;
      letter-spacing: 1px;
      line-height: 1.2;
      padding: 0;
    `;
  }}
  margin-top: 4px;
`;

const PromoAsset = styled(HTMLAsset)`
  padding: 0;
  width: 100%;
  font-size: ${props => (props.isClearanceItem ? '12' : '10')}px;
  letter-spacing: 1px;
  line-height: normal;
  ${mobile`
    padding: 0px;
  `};
`;

const ProductTextLinkWrapper = styled.div`
  ${props => (props.isBorderfreeCustomer ? `block` : `inline-block`)};

  > a {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`;

const StyledVipBoxButton = styled(VipBoxButton)`
  height: auto;
  padding: 0;
  background-color: transparent;
  border: none;
  text-transform: none;
  text-align: left;
`;

const StyledVipBoxButtonWrapper = styled.div``;

const UnavailableItem = styled(ProductName)`
  color: ${props => props.theme.colors.error};
  font-weight: 700;
`;

const BorderfreePricing = styled.div``;

const USDPrice = styled.span`
  color: ${props => props.theme.colors.default};
  font-weight: 500;
  padding-left: 4px;
  span {
    padding-left: 4px;
  }
`;

const StyledWishlistButton = styled(WishlistButton)`
  svg {
    width: 18px;
    height: 18px;
  }
`;

function ProductCell({
  className,
  extraQueryParams,
  filters,
  fplId = null,
  gridPosition,
  hideColorsAvailable = false,
  hideFlags = false,
  hideOverlay = false,
  hidePrice = false,
  hidePromo = false,
  hideReviewStars = false,
  hoverable = false,
  isOnGrid = false,
  isOnSearchGrid = false,
  isTeaser = false,
  onProductClick,
  pSource = '',
  product,
  productTrackingEntries,
  renderSmall = false,
  searchQuery,
  shouldNotBeClickable,
  shouldShowProductColor = false,
  shouldShowSpacedBorder,
  showShadow,
  sortBy,
  trackedProductIds,
  trackingSource,
}) {
  const { 'grid-pdp-pricing-updates': gridPdpPricingUpdatesEnabled } =
    useLDFlags();
  const isBorderfreeCustomer = useSelector(
    state => state.borderfree.isBorderfreeCustomer
  );
  const isRihannasPick = useSelector(state =>
    isRihannasPickProduct(state, { product })
  );
  const isNew = useSelector(state => isNewProduct(state, { product }));
  const isBestSeller = useSelector(state =>
    isBestSellerProduct(state, { product })
  );
  const isBackInStock = useSelector(state =>
    isBackInStockProduct(state, { product })
  );
  const isTopRated = useSelector(state =>
    isTopRatedProduct(state, { product })
  );
  const isTrending = useSelector(state =>
    isTrendingProduct(state, { product })
  );
  const isClf = useSelector(state => isClfProduct(state, { product }));
  const isCustomizable = useSelector(state =>
    isProductCustomizable(state, { product })
  );
  const isUnlockedXclusive = useSelector(state =>
    isUnlockedXclusiveProduct(state, { product })
  );
  const isFW = useSelector(state => isFWProduct(state, { product }));
  const isExclusive = useSelector(state =>
    isExclusiveProduct(state, { product })
  );
  const isPreorderable = useSelector(state =>
    checkIsPreorderable(state, { product })
  );
  const isYouniversal = useSelector(state =>
    isYouniversalProduct(state, { product })
  );
  const isProductSet = checkIsProductSet(product);
  const isSoldOut = useSelector(state => checkIsSoldOut(state, { product }));
  const isComingSoonProduct = useSelector(state =>
    checkIsComingSoonProduct(state, { product })
  );
  const quantity = useSelector(state =>
    getWarehouseQuantity(state, { product })
  );
  const promos = useSelector(state => getProductPromos(state, { product }));
  const shouldHideRelatedProducts = useSelector(state =>
    checkShouldHideRelatedProducts(state, { product })
  );
  const shouldShowPromoPrice = useSelector(state =>
    getShouldShowPromoPrice(state, { product })
  );
  const isClearanceItem = useSelector(state =>
    checkIsClearanceItem(state, { product })
  );
  const shouldProductUseCustom = useSelector(state =>
    shouldProductUseCustomTag(state, { product })
  );
  const { isInfluencer } = useSelector(state => state.customer);

  const { isLead, isPaygo, isVip } = useMembership();

  const warehouseLocation = useSelector(getRegion);
  const isLowStockEligible =
    locationLowStockIndicatorMasterProduct[warehouseLocation];

  const getPriceDetails = () => {
    const { default_unit_price: defaultPrice, retail_unit_price: retailPrice } =
      product;
    let displayPrice;
    let crossedOutPrice;

    if (isBorderfreeCustomer) {
      displayPrice = retailPrice;
    } else if (shouldShowPromoPrice) {
      displayPrice = promos.promo_price || promos.promoPrice;
      crossedOutPrice = isVip ? defaultPrice : retailPrice;
    } else {
      displayPrice = defaultPrice;
    }
    return { displayPrice, shouldShowPromoPrice, crossedOutPrice };
  };

  const renderPriceAmount = asset => {
    const overlayText =
      asset && asset.options.customVars.overlayText
        ? asset.options.customVars.overlayText
        : null;
    const flags = asset && toStringSet(asset.options.customVars.flags, true);
    const bundle = asset && flags.has('bundle');
    const { displayPrice, shouldShowPromoPrice, crossedOutPrice } =
      getPriceDetails();
    const shouldShowCrossedOutPrice =
      gridPdpPricingUpdatesEnabled &&
      crossedOutPrice > 0 &&
      crossedOutPrice !== displayPrice;

    const crossOut = shouldShowPromoPrice
      ? false
      : asset && flags.has('crossout');

    return (
      <PriceWrapper
        isBundle={bundle}
        isClearanceItem={isClearanceItem}
        $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
      >
        {isVip ? (
          <StyledPrice
            amount={displayPrice}
            old={crossOut}
            isPromoprice={shouldShowPromoPrice}
            isBundle={bundle}
            isClearanceItem={isClearanceItem}
          />
        ) : (
          <BorderfreePricing>
            <StyledPrice
              amount={displayPrice}
              old={crossOut}
              isPromoprice={shouldShowPromoPrice}
              isBundle={bundle}
              isBorderfreeCustomer
              isClearanceItem={isClearanceItem}
            />
            {isBorderfreeCustomer && (
              /* for BF, also show an untranslated USD price */
              <USDPrice>
                / USD
                <span>${displayPrice}</span>
              </USDPrice>
            )}
          </BorderfreePricing>
        )}
        {bundle ? (
          <PromoTextBundle>
            {!!overlayText && (
              <>
                <OptionText>
                  <FormattedMessage id="site_pdp.or" defaultMessage="or" />
                </OptionText>
                {overlayText}
              </>
            )}
          </PromoTextBundle>
        ) : null}
        {shouldShowCrossedOutPrice ? (
          <StyledPrice amount={crossedOutPrice} $showStrikethrough />
        ) : null}
      </PriceWrapper>
    );
  };

  // moved from renderPriceAmount
  const renderPromoLabel = asset => {
    const flags = asset && toStringSet(asset.options.customVars.flags, true);
    const bundle = asset && flags.has('bundle');
    const { shouldShowPromoPrice } = getPriceDetails();

    const promoPriceLabel = isProductSet ? (
      isVip ? (
        <FormattedMessage
          id="site_pdp.vip_xclusive_sets_price"
          defaultMessage="VIP XCLUSIVE SET"
        />
      ) : (
        <FormattedMessage
          id="site_pdp.non_vip_sets_price"
          defaultMessage="NEW VIP SET"
        />
      )
    ) : isVip ? (
      <FormattedMessage
        id="site_pdp.xclusive_vip_price"
        defaultMessage="VIP XCLUSIVE"
      />
    ) : (
      <FormattedMessage
        id="site_pdp.offer_non_vip_price"
        defaultMessage="NEW VIP OFFER"
      />
    );

    return (
      <PriceWrapper
        isBundle={bundle}
        isClearanceItem={isClearanceItem}
        $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
      >
        {shouldShowPromoPrice && !asset ? (
          <PromoLabel
            $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
          >
            {promoPriceLabel}
          </PromoLabel>
        ) : null}
      </PriceWrapper>
    );
  };

  const renderFlags = () => {
    let flag;
    const islowStockElibile = quantity <= isLowStockEligible.lowStockMax;
    if (!isSoldOut && !islowStockElibile && shouldProductUseCustom) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.custom_tag_label"
            defaultMessage="Custom Tag"
          />
        </ProductFlag>
      );
    } else if (isPreorderable) {
      flag = (
        <ProductFlag productTag="shopItEarly">
          <FormattedMessage
            id="global_checkout.pre_order_label"
            defaultMessage="Pre-order"
          />
        </ProductFlag>
      );
    } else if (isCustomizable) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.customizable"
            defaultMessage="Customizable"
          />
        </ProductFlag>
      );
    } else if (isUnlockedXclusive) {
      flag = (
        <ProductFlag productTag="unlockedExclusive">
          <FormattedMessage
            id="global_checkout.unlocked_xclusive_label"
            defaultMessage="Unlocked Xclusive"
          />
        </ProductFlag>
      );
    } else if (isComingSoonProduct) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.coming_soon_label"
            defaultMessage="Coming Soon"
          />
        </ProductFlag>
      );
    } else if (!isSoldOut && !isProductSet && islowStockElibile) {
      flag = <ProductCellLowStockFlag isMasterProduct quantity={quantity} />;
    } else if (isYouniversal) {
      flag = (
        <ProductFlag productTag="youniversal">
          <FormattedMessage
            id="global_checkout.youniversal"
            defaultMessage="YOUniversal"
          />
        </ProductFlag>
      );
    } else if (isRihannasPick) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.rihannas_pick_label"
            defaultMessage="Rihanna’s Pick"
          />
        </ProductFlag>
      );
    } else if (isBackInStock) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.back_in_stock_label"
            defaultMessage="Back in Stock"
          />
        </ProductFlag>
      );
    } else if (isBestSeller) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.best_seller_label"
            defaultMessage="Best seller"
          />
        </ProductFlag>
      );
    } else if (isTopRated) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.top_rated_label"
            defaultMessage="Top Rated"
          />
        </ProductFlag>
      );
    } else if (isNew) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.new_label"
            defaultMessage="New"
          />
        </ProductFlag>
      );
    } else if (isTrending) {
      flag = (
        <ProductFlag>
          <FormattedMessage
            id="global_checkout.trending_label"
            defaultMessage="Trending"
          />
        </ProductFlag>
      );
    } else if (isFW) {
      flag = <FWFlag />;
    } else if (isExclusive && !isBorderfreeCustomer) {
      flag = <XclusiveFlag shortLabel data-qa-automation="gridItemExclusive" />;
    } else if (isClf) {
      flag = <CLFFlag />;
    } else {
      return null;
    }
    return <FlagWrapper>{flag}</FlagWrapper>;
  };

  const renderOverlay = () => {
    if (isComingSoonProduct) {
      return (
        <ComingSoonContainer>
          <ComingSoonOverlay>
            <FormattedMessage
              id="global_checkout.coming_soon_label"
              defaultMessage="Coming Soon"
            />
          </ComingSoonOverlay>
        </ComingSoonContainer>
      );
    } else if (isSoldOut) {
      return (
        <SoldOutContainer>
          <SoldOutOverlay>
            <FormattedMessage
              id="global_checkout.sold_out"
              defaultMessage="Sold Out"
            />
          </SoldOutOverlay>
        </SoldOutContainer>
      );
    }

    return null;
  };

  const renderPromoText = asset => {
    if (asset) {
      const htmlText = asset.options.htmlText ? asset.options.htmlText : null;
      const overlayText = asset.options.customVars.overlayText
        ? asset.options.customVars.overlayText
        : null;
      const bundle =
        asset &&
        toStringSet(asset.options.customVars.flags, true).has('bundle');

      return (
        <PromoTextWrapper
          isClearanceItem={isClearanceItem}
          $gridPdpPricingUpdatesEnabled={gridPdpPricingUpdatesEnabled}
        >
          {htmlText ? (
            <PromoAsset isClearanceItem={isClearanceItem} asset={asset} />
          ) : null}
          {!bundle && overlayText ? <PromoText>{overlayText}</PromoText> : null}
        </PromoTextWrapper>
      );
    }

    return null;
  };

  const renderColorsAvailable = () => {
    const {
      related_product_count: relatedProductCount,
      related_product_id_object_list: relatedProducts,
      tag_id_list: tagIdList,
    } = product;

    let shouldRenderColors = false;
    const hasRelatedProducts =
      (isOnSearchGrid && relatedProductCount && relatedProductCount > 0) ||
      (relatedProducts && relatedProducts.length > 1);

    if (!shouldHideRelatedProducts) {
      if (
        (!isProductSet && hasRelatedProducts) ||
        (tagIdList &&
          tagIdList
            .map(item => parseInt(item)) // search grid response to tagList is array of strings whereas the
            .includes(multiColors)) // where as the default grid response is int.
      ) {
        shouldRenderColors = true;
      }
    }

    if (shouldRenderColors && !isClearanceItem) {
      return (
        <ColorsText>
          +{' '}
          <FormattedMessage
            id="site_product_option.colors_available"
            defaultMessage="{colorsAvailable} colors available"
            values={{
              colorsAvailable: isOnSearchGrid
                ? relatedProductCount + 1
                : relatedProducts?.length || 0,
            }}
          />
        </ColorsText>
      );
    }
  };

  const renderReviewStars = () => {
    let {
      master_product_reviews_count: masterReviewCount,
      review_count: reviewCount,
      average_overall_review_filtered_stores: averageRating,
      // FIXME: temporary to render stars on search grid, should be using average_overall_review_filtered_stores
      average_review_all_stores: averageReviewAllStores,
    } = product;

    if (isOnSearchGrid) {
      reviewCount = masterReviewCount;
      // FIXME: temporary to render stars on search grid, should be using average_overall_review_filtered_stores
      averageRating = averageReviewAllStores;
    }

    if (reviewCount && reviewCount > 0) {
      return <StyledStars readonly initialRating={averageRating || 0} />;
    }

    return null;
  };

  const handleProductClick = () => {
    if (onProductClick) {
      onProductClick(product);
    }
  };

  const { displayPrice } = getPriceDetails();

  const shouldHideReviewStars =
    isPreorderable || hideReviewStars || (isLead && !isInfluencer) || isPaygo;

  return (
    <Container
      data-qa-automation="gridSetsItem"
      shouldShowSpacedBorder={shouldShowSpacedBorder}
      className={className}
      renderSmall={renderSmall}
    >
      <ThumbContainer shouldShowSpacedBorder={shouldShowSpacedBorder}>
        <ProductImpression
          fplId={fplId}
          isOnGrid={isOnGrid}
          product={product}
          gridPosition={gridPosition}
          pageSource={pSource}
          price={displayPrice}
          trackingSource={trackingSource}
          filters={filters}
          sortBy={sortBy}
          productTrackingEntries={productTrackingEntries}
          trackedProductIds={trackedProductIds}
        >
          {!isTeaser ? (
            <WishlistContext.Consumer>
              {context => {
                return (
                  <ThumbHeader>
                    {hoverable && !context.hideWishlist ? (
                      <StyledWishlistButton
                        overlay
                        product={product}
                        eventCategory="Grids"
                      />
                    ) : null}
                  </ThumbHeader>
                );
              }}
            </WishlistContext.Consumer>
          ) : null}
          {isTeaser ? (
            <StyledVipBoxButtonWrapper>
              <StyledVipBoxButton>
                <ThumbnailNewRatioWrapper showShadow={showShadow}>
                  <ThumbnailImageWrapper>
                    <Thumbnail size={8} product={product} lazyLoad />
                    <ThumbnailLaydown
                      size={8}
                      product={product}
                      angle="LAYDOWN"
                      lazyLoad
                    />
                  </ThumbnailImageWrapper>
                </ThumbnailNewRatioWrapper>
              </StyledVipBoxButton>
            </StyledVipBoxButtonWrapper>
          ) : (
            <ProductLink
              product={product}
              extraQueryParams={extraQueryParams}
              searchQuery={searchQuery}
              shouldNotBeClickable={shouldNotBeClickable}
            >
              {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
              <a
                onClick={handleProductClick}
                data-autotag="product-cell-thumb"
                data-qa-automation="myacctSkipModalMyGrid_EU"
              >
                <ThumbnailNewRatioWrapper showShadow={showShadow}>
                  <ThumbnailImageWrapper>
                    <Thumbnail
                      size={8}
                      product={product}
                      angle={shouldShowSpacedBorder ? 'LAYDOWN' : '1'}
                      lazyLoad
                    />
                    <ThumbnailLaydown
                      size={8}
                      product={product}
                      angle="LAYDOWN"
                      lazyLoad
                    />
                  </ThumbnailImageWrapper>
                </ThumbnailNewRatioWrapper>

                {!hideOverlay && renderOverlay()}
                {!isSoldOut && !hideFlags && renderFlags()}
              </a>
            </ProductLink>
          )}
        </ProductImpression>
      </ThumbContainer>
      <Details isOnGrid={isOnGrid} isClearanceItem={isClearanceItem}>
        <AssetContainer
          name="pdp_html_fpl"
          filter={parentFilter => {
            return (asset, index, assets) => {
              if (!parentFilter(asset, index, assets)) {
                return false;
              }
              const fplId = asset.options.customVars.fplid;
              if (fplId) {
                return (
                  product.featured_product_location_id_list &&
                  product.featured_product_location_id_list.includes(fplId)
                );
              }
              return true;
            };
          }}
        >
          {asset => (
            <ProductTextLinkWrapper>
              {isTeaser ? (
                <StyledVipBoxButton>
                  <ProductName>{product.label}</ProductName>
                </StyledVipBoxButton>
              ) : (
                <>
                  <ProductLink
                    product={product}
                    extraQueryParams={extraQueryParams}
                    searchQuery={searchQuery}
                    shouldNotBeClickable={shouldNotBeClickable}
                  >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <a onClick={handleProductClick}>
                      {!hidePrice && renderPromoLabel(asset.data)}
                      {!hidePromo && renderPromoText(asset.data)}
                      <ProductName
                        data-qa-automation="gridSetsProdName"
                        isClearanceItem={isClearanceItem}
                        $gridPdpPricingUpdatesEnabled={
                          gridPdpPricingUpdatesEnabled
                        }
                      >
                        {product.label.replace(/&amp;/g, '&')}
                      </ProductName>
                      {!hidePrice && renderPriceAmount(asset.data)}
                      {!hideColorsAvailable && renderColorsAvailable()}
                      {shouldShowProductColor && (
                        <ColorsText>{product.color}</ColorsText>
                      )}
                    </a>
                  </ProductLink>
                  <ProductLink
                    product={product}
                    extraQueryParams={extraQueryParams}
                    searchQuery={searchQuery}
                    shouldNotBeClickable={shouldNotBeClickable}
                  >
                    <a>
                      {!shouldHideReviewStars && renderReviewStars()}
                      {isPreorderable && isBorderfreeCustomer && (
                        <UnavailableItem>
                          <FormattedMessage
                            id="site_pdp.border_free_plp_unavailable"
                            defaultMessage="Not Available"
                          />
                        </UnavailableItem>
                      )}
                    </a>
                  </ProductLink>
                </>
              )}
            </ProductTextLinkWrapper>
          )}
        </AssetContainer>
      </Details>
    </Container>
  );
}

ProductCell.Container = Container;
ProductCell.Details = Details;
ProductCell.FlagWrapper = FlagWrapper;
ProductCell.PriceWrapper = PriceWrapper;
ProductCell.ProductTextLinkWrapper = ProductTextLinkWrapper;
ProductCell.PromoTextWrapper = PromoTextWrapper;
ProductCell.PromoAsset = PromoAsset;
ProductCell.ThumbnailNewRatioWrapper = ThumbnailNewRatioWrapper;
ProductCell.ThumbContainer = ThumbContainer;
ProductCell.Thumbnail = Thumbnail;
ProductCell.ThumbnailLaydown = ThumbnailLaydown;

ProductCell.propTypes = {
  /**
   * Class name(s) to apply to the container element.
   */
  className: PropTypes.string,
  extraQueryParams: PropTypes.object,
  filters: PropTypes.object,
  fplId: PropTypes.number,
  /**
   * The position of this cell in the grid. Tracking impressions needs this.
   */
  gridPosition: PropTypes.number,
  hideColorsAvailable: PropTypes.bool,
  hideFlags: PropTypes.bool,
  hideOverlay: PropTypes.bool,
  hidePrice: PropTypes.bool,
  hidePromo: PropTypes.bool,
  hideReviewStars: PropTypes.bool,
  /**
   * Determines whether extra controls like Quick View and Wishlist are shown
   * when hovering the product.
   */
  hoverable: PropTypes.bool,
  isOnGrid: PropTypes.bool,
  /**
   * Whether or not this cell is on the search grid
   */
  isOnSearchGrid: PropTypes.bool,
  isTeaser: PropTypes.bool,
  onProductClick: PropTypes.func,
  /**
   * Product data to render.
   */
  pSource: PropTypes.string,
  product: PropTypes.object,
  productTrackingEntries: PropTypes.array,
  searchQuery: PropTypes.string,
  shouldNotBeClickable: PropTypes.bool,
  shouldShowProductColor: PropTypes.bool,
  shouldShowSpacedBorder: PropTypes.bool,
  showShadow: PropTypes.bool,
  sortBy: PropTypes.string,
  trackedProductIds: PropTypes.array,
  trackingSource: PropTypes.string,
};

export default ProductCell;
ProductCell.Container = Container;
